import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, SimpleGrid } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, DECIMAL_PROPS, NumberInput, Select, YesNoRadioButton } from "components";
import { CreditOptionsResponse, CreditRequestChildResponse, CreditResponse } from "types";
import { AppUtils } from "utils";
import { useCreditRequestMode } from "../request-mode-context";

import SectionTitle, { SectionTitleIndicatorType } from "../../../components/section-title";

interface CreditRequestPersonalChildrenProps {
  credit: CreditResponse;
  form: any;
  options: CreditOptionsResponse;
  readOnly?: boolean;
  hasWarning?: boolean;
  onWarningClick?: () => void;
  indicatorType?: SectionTitleIndicatorType;
  syncChildrenValues?: CreditRequestChildResponse[];
  syncSupportsChildren?: boolean;
  syncVersion?: number;
}

interface ChildObject {
  creditRequestId?: number;
  birthYear?: number;
  craft?: boolean;
  craftAmount?: number;
}
const tempChildObject = {
  creditRequestId: undefined,
  birthYear: undefined,
  craft: undefined,
  craftAmount: undefined,
} as ChildObject;

const propertyName = "children";

const normalizeBirthYear = (birthYear: number | string | null | undefined) => {
  if (birthYear === undefined || birthYear === null || birthYear === "") {
    return undefined;
  }

  const normalizedBirthYear =
    typeof birthYear === "string" ? Number(birthYear) : birthYear;

  return Number.isNaN(normalizedBirthYear) ? undefined : normalizedBirthYear;
};

const CreditRequestPersonalChildren = ({
  credit,
  form,
  options,
  readOnly = false,
  hasWarning = false,
  onWarningClick,
  indicatorType = "warning",
  syncChildrenValues,
  syncSupportsChildren,
  syncVersion = 0,
}: CreditRequestPersonalChildrenProps) => {
  const { t } = useTranslation();
  const { isPreliminary } = useCreditRequestMode();
  const [visibleFields, setVisibleFields] = useState<string[]>(
    AppUtils.getVisibleChildrenFields(credit[propertyName]),
  );
  const [supportsChildren, setSupportsChildren] = useState<boolean | undefined>(
    credit.supportsChildren,
  );

  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }
    const supports = formValues.supportsChildren;
    if (!supports && childrenForm.values.length > 0) {
      childrenForm.setInitialValues([]);
      childrenForm.reset();
    }
    setSupportsChildren(supports);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getValues().supportsChildren]);

  const childrenForm = useForm({
    mode: "uncontrolled",
    initialValues:
      credit[propertyName] && credit[propertyName].length > 0
        ? credit[propertyName].map((el) => ({
            ...el,
            birthYear: normalizeBirthYear(el.birthYear),
          }))
        : [],
    onValuesChange(values, previous) {
      if (values[values.length - 1] !== tempChildObject) {
        updateCredit(values, previous);
      }
    },
  });

  useEffect(() => {
    const nextChildren =
      syncChildrenValues && syncChildrenValues.length > 0
        ? syncChildrenValues.map((el) => ({
            ...el,
            birthYear: normalizeBirthYear(el.birthYear),
          }))
        : [];
    childrenForm.setInitialValues(nextChildren);
    childrenForm.setValues(nextChildren);
    setVisibleFields(AppUtils.getVisibleChildrenFields(nextChildren));
    setSupportsChildren(syncSupportsChildren ?? credit.supportsChildren);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncVersion]);

  const updateCredit = useCallback(
    async (values: any, previous: any) => {
      const prevVisibleFields = AppUtils.getVisibleChildrenFields(previous);
      const newVisibleFields = AppUtils.getVisibleChildrenFields(values);
      AppUtils.clearArrayFieldsWhenUpdating(values, previous, newVisibleFields);
      AppUtils.updateArrayFieldsWhenVisibilityChanges(
        prevVisibleFields,
        newVisibleFields,
        values,
        childrenForm,
      );

      const tempArray: ChildObject[] = [];
      Object.keys(values).forEach((key) => {
        const item: ChildObject = { ...values[key] };
        item.birthYear = normalizeBirthYear(item.birthYear);
        const isNotEmpty = item.birthYear || item.craft || item.craftAmount;
        if (isNotEmpty) {
          item.creditRequestId = credit.id;
          tempArray.push(item);
        }
      });
      form.setFieldValue(propertyName, tempArray);
      setVisibleFields(newVisibleFields);
    },
    [childrenForm, credit.id, form],
  );

  const childrenValidYears = useMemo(() => {
    const birthYear = form.values.birthDate?.getFullYear() ?? new Date().getFullYear() - 25;
    const currentYear = new Date().getFullYear();
    const minYear = Math.max(birthYear, currentYear - 25);
    return Array.from({ length: currentYear - minYear + 1 }, (_, i) => minYear + i);
  }, [form.values.birthDate]);

  const childrenView = useMemo(() => {
    const transformed = childrenForm.getTransformedValues();
    const length = Object.keys(transformed).length;
    return Object.keys(transformed).map((el: any, index: number) => (
      <SimpleGrid cols={4} mt={"sm"} key={el}>
        <Select
          label={t("credit.children.birthYear.label", { number: index + 1 })}
          data={childrenValidYears.map((el) => ({
            value: el.toString(),
            label: el.toString(),
          }))}
          key={childrenForm.key(`${index}.birthYear`)}
          searchable
          defaultValue={
            childrenForm.getValues()?.[index]?.birthYear
              ? String(childrenForm.getValues()[index].birthYear)
              : null
          }
          onChange={(value) =>
            childrenForm.setFieldValue(
              `${index}.birthYear`,
              normalizeBirthYear(value),
            )
          }
          error={childrenForm.errors[`${index}.birthYear`]}
          clearable={true}
          disabled={readOnly}
        />
        {visibleFields.includes(`children.${index}.craft`) ? (
          <YesNoRadioButton
            label={t("credit.children.craft.label")}
            name={`${index}.craft`}
            form={childrenForm}
            disabled={readOnly}
          />
        ) : (
          <Box />
        )}

        {visibleFields.includes(`children.${index}.craftAmount`) ? (
          <NumberInput
            label={t("credit.children.craftAmount.label")}
            key={childrenForm.key(`${index}.craftAmount`)}
            {...childrenForm.getInputProps(`${index}.craftAmount`)}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
        ) : (
          <Box />
        )}
        {length > 0 && !readOnly && (
          <Button
            variant={"transparent"}
            size={"compact-sm"}
            color={"var(--mantine-color-error)"}
            mt={14}
            style={{ alignSelf: "center" }}
            onClick={() => AppUtils.removeObject(childrenForm, index, undefined)}
          >
            <IconTrash size={20} />
          </Button>
        )}
      </SimpleGrid>
    ));
  }, [childrenForm, t, childrenValidYears, readOnly, visibleFields]);

  const transformedLength = Object.keys(childrenForm.getTransformedValues()).length;

  return (
    <Box>
      <SectionTitle
        label={t("credit.children.label")}
        hasWarning={hasWarning}
        onWarningClick={onWarningClick}
        indicatorType={indicatorType}
      />
      <SimpleGrid cols={4}>
        <Select
          label={t("user.maritalStatus.label")}
          data={options["maritalStatus"].map((el) => ({
            value: el,
            label: t(`user.maritalStatus.options.${el}`),
          }))}
          key={form.key("maritalStatus")}
          {...form.getInputProps("maritalStatus")}
          clearable={true}
          required={!isPreliminary}
          disabled={readOnly}
        />
        <YesNoRadioButton
          label={t("credit.supportsChildren.label")}
          name={"supportsChildren"}
          form={form}
          disabled={readOnly}
        />
      </SimpleGrid>
      {childrenView}

      {supportsChildren && transformedLength < 4 && !readOnly && (
        <Button
          onClick={() => AppUtils.addNewObject(childrenForm, tempChildObject)}
          size={"compact-xs"}
          variant={"transparent"}
          mt={"md"}
        >
          <>
            <Box mr={4}>
              <IconPlus size={16} />
            </Box>
            {t("credit.children.add")}
          </>
        </Button>
      )}
    </Box>
  );
};

export default CreditRequestPersonalChildren;
